import { BuildStatus } from '@cadstrom/cadstrom-shared';

import type { RevisionStatus } from '@cadstrom/cadstrom-shared';

/** The revision facts the customer-facing status is derived from. */
export interface RevisionDisplayStatusContext {
	status: RevisionStatus;
	/** The status the revision failed from; absent when not errored or not recorded. */
	erroredFromStatus?: RevisionStatus;
}

/**
 * The status the customer-facing pages present a revision as being in.
 *
 * An errored revision presents as the status it failed from, so the page keeps the layout
 * and wording it had before the failure. A failure with no recorded origin presents as
 * `processing`, the earliest stage it could have been in.
 *
 * @param revision - The revision's status and error origin.
 * @returns The status to present.
 */
export function displayedRevisionStatus(revision: RevisionDisplayStatusContext): RevisionStatus {
	if (revision.status !== BuildStatus.Error) {
		return revision.status;
	}
	return revision.erroredFromStatus ?? BuildStatus.Processing;
}

/**
 * The status to present to a given viewer: the real status for staff, who act on
 * failures, and {@link displayedRevisionStatus} for everyone else.
 *
 * @param revision - The revision's status and error origin.
 * @param viewer - Whether the viewer is staff.
 * @returns The status to present.
 */
export function displayedRevisionStatusFor(
	revision: RevisionDisplayStatusContext,
	viewer: { isStaff: boolean },
): RevisionStatus {
	return viewer.isStaff ? revision.status : displayedRevisionStatus(revision);
}
